
import { useState, useRef, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/types/auth';
import { profileLogger } from '@/utils/debug';

// Interface for profile fetch result
export interface ProfileFetchResult {
  success: boolean;
  error?: string;
  profile?: Profile | null;
  retryable?: boolean;
}

export const useProfileManager = () => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const currentUserId = useRef<string | null>(null);
  const profileCache = useRef<Profile | null>(null);
  const isFetching = useRef(false);
  const isMounted = useRef(true);

  // Helper to detect network-specific errors
  const isNetworkError = useCallback((error: any): boolean => {
    if (!error) return false;
    const errorMessage = error?.message || String(error);
    return (
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('WebSocket') ||
      errorMessage.includes('ECONNREFUSED') ||
      errorMessage.includes('ETIMEDOUT') ||
      errorMessage.includes('fetch failed') ||
      !navigator.onLine
    );
  }, []);

  const fetchProfile = useCallback(async (userId: string): Promise<ProfileFetchResult> => {
    // Skip if we already have this user's profile cached
    if (currentUserId.current === userId && profileCache.current) {
      profileLogger.log('🔥 Using cached profile for user:', userId);
      if (isMounted.current) {
        setProfile(profileCache.current);
      }
      return { success: true, profile: profileCache.current };
    }

    // Prevent concurrent fetches for the same user
    if (isFetching.current) {
      profileLogger.log('🔥 Already fetching profile, skipping');
      return { success: false, error: 'Already fetching profile', retryable: false };
    }

    try {
      isFetching.current = true;
      profileLogger.log('🔥 Fetching profile for user:', userId);

      // Use select without .maybeSingle() to avoid the problematic Accept header
      // Instead, we'll handle the array response and take the first item
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .limit(1);

      if (error) {
        profileLogger.error('🔥 Error fetching profile:', error);
        profileLogger.captureError(error);
        if (isMounted.current) {
          setProfile(null);
        }
        // Don't clear cache on error - preserve last known good state
        currentUserId.current = null;

        const retryable = isNetworkError(error);
        return {
          success: false,
          error: error.message,
          profile: null,
          retryable
        };
      }

      // Handle the array response - take first item or null if empty
      const profileData = data && data.length > 0 ? data[0] : null;

      profileLogger.log('🔥 Profile fetched successfully:', profileData);
      if (isMounted.current) {
        setProfile(profileData);
      }
      profileCache.current = profileData;
      currentUserId.current = userId;

      return { success: true, profile: profileData };
    } catch (error) {
      profileLogger.error('🔥 Error in fetchProfile:', error);
      profileLogger.captureError(error);
      if (isMounted.current) {
        setProfile(null);
      }
      // Don't clear cache on error - preserve last known good state
      currentUserId.current = null;

      const retryable = isNetworkError(error);
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error),
        profile: null,
        retryable
      };
    } finally {
      isFetching.current = false;
    }
  }, [isNetworkError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const clearProfile = useCallback(() => {
    setProfile(null);
    profileCache.current = null;
    currentUserId.current = null;
    isFetching.current = false;
  }, []);

  return {
    profile,
    fetchProfile,
    clearProfile,
    setProfile,
    isFetchingProfile: () => isFetching.current
  };
};
